import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2ListCurrencysForAccountQuery } from "@generated";
import { RateTableForm } from "../RateTableForm/RateTableForm";
import { useRateTableMutations, useRateTablesList } from "../api";
import { emptyRateTableForm } from "../types";
import type { RateTableFormValues } from "../types";

const FORM_ID = "rate-table-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useRateTableMutations();
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  const nextAction = useRef<"edit" | "new">("edit");

  const { data: currencyData, isLoading: currenciesLoading } =
    useV2ListCurrencysForAccountQuery({
      filter: { active: true },
    });
  const { pageMeta } = useRateTablesList({ filter: { active: true } });

  const currencyOptions = useMemo(
    () =>
      (currencyData?.data ?? []).map((c: any) => ({
        value: String(c.id),
        label: c.attributes?.name ?? "",
      })),
    [currencyData]
  );

  // First rate table is implicitly default; only offer the toggle once more exist.
  const showDefaultToggle = pageMeta.recordCount > 0;
  const showCurrencySelect = currencyOptions.length > 1;
  // When there is a single currency, send it silently.
  const initialValues: RateTableFormValues = {
    ...emptyRateTableForm,
    currencyId: showCurrencySelect ? "" : currencyOptions[0]?.value ?? "",
  };

  const handleSubmit = async (values: RateTableFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: `'${values.name}' saved` });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: { variant: "success", text: `'${values.name}' saved` },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create rate table", err);
      setToast(saveErrorToast(err, "rate table"));
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  // RateTableForm copies initialValues into state once, so building it before
  // the currencies arrive would drop the single-currency default for good.
  if (currenciesLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminFormPage
        title="Add a Rate Table"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <RateTableForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          currencyOptions={currencyOptions}
          showCurrencySelect={showCurrencySelect}
          showDefaultToggle={showDefaultToggle}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
